/**
 * 业务枚举集中定义（规范 24）。
 *
 * 命名：
 *   - XXX                常量值（number 或 string，来自后端协议）
 *   - XXX_LABEL          UI 渲染映射 { [value]: { text, tag } }
 *                        tag ∈ 'success' | 'warning' | 'danger' | 'info' | '' (default)
 *                        直接传给 YtTable columns[i].labelMap 或 el-tag :type
 *
 * 新增枚举时：先在此文件定义常量与 label，再在页面 import，严禁裸魔数。
 */

// -- 水质组态 ---------------------------------------------------------------
export const WATER_QUALITY_PERIOD = Object.freeze({ HOUR: 'hour', TODAY: 'today', WEEK: 'week' })
export const WATER_QUALITY_RULE = Object.freeze({ READY: 'ready', UNCONFIGURED: 'unconfigured', INVALID: 'invalid_rule' })
export const WATER_QUALITY_STATUS_LABEL = Object.freeze({
  collected: { key: 'waterQuality.collected', tag: 'info' },
  normal: { key: 'waterQuality.normal', tag: 'success' },
  alarm: { key: 'waterQuality.alarm', tag: 'danger' },
  no_data: { key: 'waterQuality.noData', tag: 'info' },
})
export const CLOUD_TEMPLATE = Object.freeze({ DENTAL: 7 })

// -- 报警级别 ---------------------------------------------------------------
export const ALARM_LEVEL = Object.freeze({
  GENERAL: 1,
  IMPORTANT: 2,
  URGENT: 3,
})

export const ALARM_LEVEL_LABEL = Object.freeze({
  [ALARM_LEVEL.GENERAL]:   { text: '一般报警', tag: 'info' },
  [ALARM_LEVEL.IMPORTANT]: { text: '重要报警', tag: 'warning' },
  [ALARM_LEVEL.URGENT]:    { text: '紧急报警', tag: 'danger' },
})

// -- 报警处理状态 -----------------------------------------------------------
export const ALARM_HANDLE_STATUS = Object.freeze({
  PENDING: 0,
  HANDLED: 1,
})

export const ALARM_HANDLE_STATUS_LABEL = Object.freeze({
  [ALARM_HANDLE_STATUS.PENDING]: { text: '未处理', tag: 'warning' },
  [ALARM_HANDLE_STATUS.HANDLED]: { text: '已处理', tag: 'success' },
})

// -- 报警当前状态（是否告警中） ---------------------------------------------
export const ALARM_STATUS = Object.freeze({
  RESOLVED: 0,
  ACTIVE: 1,
})

export const ALARM_STATUS_LABEL = Object.freeze({
  [ALARM_STATUS.RESOLVED]: { text: '已恢复', tag: 'success' },
  [ALARM_STATUS.ACTIVE]:   { text: '告警中', tag: 'danger' },
})

// -- 告警通知事件 -----------------------------------------------------------
// 一条 point_alarms 生命周期会依次产生触发、恢复两条站内通知。
export const ALARM_EVENT_TYPE = Object.freeze({
  TRIGGER: 'alarm_trigger',
  RECOVER: 'alarm_recover',
})

// -- 用户启用状态 -----------------------------------------------------------
export const USER_STATUS = Object.freeze({
  DISABLED: 0,
  ENABLED: 1,
})

export const USER_STATUS_LABEL = Object.freeze({
  [USER_STATUS.DISABLED]: { text: '禁用', tag: 'info' },
  [USER_STATUS.ENABLED]:  { text: '启用', tag: 'success' },
})

// -- 用户角色（后端返回 string） --------------------------------------------
export const USER_ROLE = Object.freeze({
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  USER: 'user',
})

export const USER_ROLE_LABEL = Object.freeze({
  [USER_ROLE.SUPER_ADMIN]: { text: '超级管理员', tag: 'danger' },
  [USER_ROLE.ADMIN]:       { text: '管理员',    tag: 'warning' },
  [USER_ROLE.USER]:        { text: '普通用户',  tag: 'info' },
})

// -- 设备访问级别 -----------------------------------------------------------
export const DEVICE_ACCESS_LEVEL = Object.freeze({
  NONE: 'none',
  VIEW: 'view',
  OPERATE: 'operate',
  MANAGE: 'manage',
})

export const DEVICE_ACCESS_LEVEL_LABEL = Object.freeze({
  [DEVICE_ACCESS_LEVEL.VIEW]: '仅查看',
  [DEVICE_ACCESS_LEVEL.OPERATE]: '可操作',
  [DEVICE_ACCESS_LEVEL.MANAGE]: '可管理',
})

export function canOperateDevice(device) {
  const level = device && device.access_level
  return level === DEVICE_ACCESS_LEVEL.OPERATE || level === DEVICE_ACCESS_LEVEL.MANAGE
}

// -- 设备主状态 -------------------------------------------------------------
// Online 连接态与活跃告警是两个独立事实；DisplayStatus 仅用于列表互斥展示。
export const DEVICE_DISPLAY_STATUS = Object.freeze({
  ALARM: 'alarm',
  ONLINE: 'online',
  OFFLINE: 'offline',
})

export const DEVICE_DISPLAY_STATUS_LABEL = Object.freeze({
  [DEVICE_DISPLAY_STATUS.ALARM]: { text: '告警', tag: 'danger' },
  [DEVICE_DISPLAY_STATUS.ONLINE]: { text: '在线', tag: 'success' },
  [DEVICE_DISPLAY_STATUS.OFFLINE]: { text: '离线', tag: 'info' },
})

// -- 右上角通知中心 ---------------------------------------------------------
export const NOTIFICATION_CATEGORY = Object.freeze({
  ALARM: 'alarm',
  DEVICE_STATUS: 'deviceStatus',
})

export const DEVICE_STATUS_EVENT_TYPE = Object.freeze({
  ONLINE: 'online',
  OFFLINE: 'offline',
})

// 兼容后端逐步上线：优先读 DisplayStatus，缺失时由告警/连接字段本地推导。
export function deviceDisplayStatus(device) {
  const state = device && device.DeviceState
  if (!state) return DEVICE_DISPLAY_STATUS.OFFLINE
  if (state.DisplayStatus) return state.DisplayStatus
  if (state.HasActiveAlarm || Number(state.ActiveAlarmCount) > 0) {
    return DEVICE_DISPLAY_STATUS.ALARM
  }
  return state.Online ? DEVICE_DISPLAY_STATUS.ONLINE : DEVICE_DISPLAY_STATUS.OFFLINE
}

export function deviceDisplayStatusMeta(device) {
  const status = deviceDisplayStatus(device)
  return DEVICE_DISPLAY_STATUS_LABEL[status] || DEVICE_DISPLAY_STATUS_LABEL[DEVICE_DISPLAY_STATUS.OFFLINE]
}

// -- 设备历史数据固定按采集时间正序读取 -------------------------------------
export const HISTORY_ORDER = Object.freeze({
  ASC: 'asc',
  DESC: 'desc',
})

// -- 操作日志动作 -----------------------------------------------------------
export const OPERATION_ACTION = Object.freeze({
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
})

export const OPERATION_ACTION_LABEL = Object.freeze({
  [OPERATION_ACTION.CREATE]: { text: '创建', tag: 'success' },
  [OPERATION_ACTION.UPDATE]: { text: '更新', tag: '' },
  [OPERATION_ACTION.DELETE]: { text: '删除', tag: 'danger' },
})

// -- 业务资源类型（用于日志/权限过滤） --------------------------------------
export const RESOURCE_TYPE = Object.freeze({
  DEVICE: 'device',
  PROJECT: 'project',
  USER: 'user',
  ADMIN: 'admin',
  ALARM: 'alarm',
  ALARM_NOTIFY: 'alarm-notify',
  POINT: 'point',
  BUILDING: 'building',
  DISINFECTION_RECORD: 'disinfection_record',
  DISINFECTION_PENDING: 'disinfection_pending',
})

export const RESOURCE_TYPE_LABEL = Object.freeze({
  [RESOURCE_TYPE.DEVICE]:       { text: '设备' },
  [RESOURCE_TYPE.PROJECT]:      { text: '项目' },
  [RESOURCE_TYPE.USER]:         { text: '用户' },
  [RESOURCE_TYPE.ADMIN]:        { text: '管理' },
  [RESOURCE_TYPE.ALARM]:        { text: '告警' },
  [RESOURCE_TYPE.ALARM_NOTIFY]: { text: '通知配置' },
  [RESOURCE_TYPE.POINT]:        { text: '数据点' },
  [RESOURCE_TYPE.BUILDING]:     { text: '楼层布局' },
  [RESOURCE_TYPE.DISINFECTION_RECORD]: { text: '消毒记录' },
  [RESOURCE_TYPE.DISINFECTION_PENDING]: { text: '未消毒项' },
})

// -- 访问日志状态（登录成功 / 失败） ----------------------------------------
export const ACCESS_LOG_STATUS = Object.freeze({
  SUCCESS: 'success',
  FAIL: 'fail',
})

export const ACCESS_LOG_STATUS_LABEL = Object.freeze({
  [ACCESS_LOG_STATUS.SUCCESS]: { text: '成功', tag: 'success' },
  [ACCESS_LOG_STATUS.FAIL]:    { text: '失败', tag: 'danger' },
})

// -- 消毒原液类型（A液 / B液，多选用 CSV 存） -------------------------------
export const LIQUID_TYPE = Object.freeze({
  A: 'A',
  B: 'B',
})

export const LIQUID_TYPE_LABEL = Object.freeze({
  [LIQUID_TYPE.A]: { text: 'A液', tag: 'success' },
  [LIQUID_TYPE.B]: { text: 'B液', tag: 'warning' },
})

export const LIQUID_TYPE_OPTIONS = Object.freeze([
  { label: 'A液', value: LIQUID_TYPE.A },
  { label: 'B液', value: LIQUID_TYPE.B },
])

// -- 传感器类型（流量 / 液位 / pH，多选用 CSV 存） --------------------------
// 后续新增类型在此追加并同步后端 allowedSensorTypes（sensor_calibration.go）
export const SENSOR_TYPE = Object.freeze({
  FLOW:  'flow',
  LEVEL: 'level',
  PH:    'ph',
})

export const SENSOR_TYPE_LABEL = Object.freeze({
  [SENSOR_TYPE.FLOW]:  { text: '流量传感器', tag: 'primary' },
  [SENSOR_TYPE.LEVEL]: { text: '液位传感器', tag: 'success' },
  [SENSOR_TYPE.PH]:    { text: 'pH传感器',   tag: 'warning' },
})

export const SENSOR_TYPE_OPTIONS = Object.freeze([
  { label: '流量传感器', value: SENSOR_TYPE.FLOW },
  { label: '液位传感器', value: SENSOR_TYPE.LEVEL },
  { label: 'pH传感器',   value: SENSOR_TYPE.PH },
])

// -- 数据点 ValueType（Modbus 寄存器解析类型） ------------------------------
// 后端枚举走原始技术名（int16/uint16/...，落库与协议解析直接用），
// 前端展示统一翻译成中文+技术名，避免运维 / 非技术用户看到裸的 int16。
export const POINT_VALUE_TYPE_LABEL = Object.freeze({
  int16:   '16位有符号整数 (int16)',
  uint16:  '16位无符号整数 (uint16)',
  int32:   '32位有符号整数 (int32)',
  uint32:  '32位无符号整数 (uint32)',
  float32: '32位浮点数 (float32)',
  float64: '64位浮点数 (float64)',
})

// 给找不到映射的兜底用，避免页面出现空白。
export function pointValueTypeLabel(value) {
  return POINT_VALUE_TYPE_LABEL[value] || value || '-'
}

// 列表场景用的短标签：只保留中文，剥掉 "(int16)" 等技术名后缀
// 表单下拉仍走 pointValueTypeLabel，确认类型时技术名能避免歧义
export function pointValueTypeShortLabel(value) {
  const full = POINT_VALUE_TYPE_LABEL[value]
  if (!full) return value || '-'
  return full.replace(/\s*\(.+?\)\s*$/, '')
}

// -- 数据点大小端（字节序）按数据类型可选项 --------------------------------
// 理论上 16 位（单寄存器）只有 abcd(不换)/dcba(换) 两种排列，cdab/badc 是"字交换"，
// 对单寄存器是空操作（cdab≡abcd），所以一度对 16 位隐藏了 cdab。
// 但现场反馈某些 16 位点位"只有 cdab 出数据、abcd/dcba 都没数据"——而 endian 根本
// 不影响"能否读到"(只影响数值)，疑似 RS485/配置重推时机，根因未定位清楚前先**放开**
// 全部三项，别挡住现场唯一可用的选择。待固件侧查明后，恢复过滤只需改回此函数即可。
const POINT_16BIT_VALUE_TYPES = Object.freeze(['int16', 'uint16'])

export function pointIs16BitType(valueType) {
  return POINT_16BIT_VALUE_TYPES.includes(valueType)
}

// 暂时放开：所有数据类型都给完整 endian 选项（abcd/dcba/cdab），不再按 16 位过滤。
// 仍接受 valueType/keepValue 形参（调用点照传），便于日后恢复过滤时少改动。
export function filterEndianOptions(options) {
  return Array.isArray(options) ? options : []
}

// -- 数据点读写权限 ---------------------------------------------------------
// 后端 permission 列为 ENUM('read','write','read_write')，前端仅翻译展示。
export const POINT_PERMISSION_LABEL = Object.freeze({
  read:       '只读',
  write:      '只写',
  read_write: '读写',
})

export function pointPermissionLabel(value) {
  return POINT_PERMISSION_LABEL[value] || value || '-'
}

// -- 点位历史存储策略 -------------------------------------------------------
// 仅控制后端 devices_data 历史落库；不影响采集周期、实时值或告警。
export const POINT_STORAGE_POLICY = Object.freeze({
  EVERY: 'every',
  CHANGE: 'change',
  CHANGE_SNAPSHOT: 'change_snapshot',
  NONE: 'none',
})

export const POINT_STORAGE_POLICY_OPTIONS = Object.freeze([
  { value: POINT_STORAGE_POLICY.EVERY, label: '每次存储', tip: '每次采集都写入历史，适合连续曲线' },
  { value: POINT_STORAGE_POLICY.CHANGE, label: '变化时存储', tip: '首次及值变化时写入，适合开关、状态和参数' },
  { value: POINT_STORAGE_POLICY.CHANGE_SNAPSHOT, label: '变化 + 30分钟快照', tip: '变化立即写入，不变时每30分钟补一条' },
  { value: POINT_STORAGE_POLICY.NONE, label: '不存历史', tip: '只保留实时值与告警，不写入历史表' },
])

export function pointStoragePolicyLabel(value) {
  const hit = POINT_STORAGE_POLICY_OPTIONS.find(item => item.value === value)
  return hit ? hit.label : '每次存储'
}

// -- Modbus 读取功能码 ------------------------------------------------------
// 数值即 Modbus 协议里的实际功能码 (FC)。
//   0x01 读线圈              (1 bit 可读写)
//   0x02 读离散输入          (1 bit 只读)
//   0x03 读保持寄存器        (16 bit 可读写)
//   0x04 读输入寄存器        (16 bit 只读，常用于传感器测量值)
// svr goose 00009 已重映射老库里的 1/2 到新语义。
export const MODBUS_CMD = Object.freeze({
  READ_COIL:           1,
  READ_DISCRETE_INPUT: 2,
  READ_HOLDING_REG:    3,
  READ_INPUT_REG:      4,
})

// 下拉标签带上"数据区(0X/1X/3X/4X)"和"读/写功能码"，让配置时一眼看清选的是哪块表、
// 能不能写：要下发的点位只能选可写区（线圈 0X / 保持寄存器 4X），只读区(1X/3X)下发会被拒。
export const MODBUS_CMD_LABEL = Object.freeze({
  [MODBUS_CMD.READ_COIL]:           '读线圈 · 01H/05H (0X)',
  [MODBUS_CMD.READ_DISCRETE_INPUT]: '读离散输入 · 02H (1X·只读)',
  [MODBUS_CMD.READ_HOLDING_REG]:    '读保持寄存器 · 03H/06H/10H (4X)',
  [MODBUS_CMD.READ_INPUT_REG]:      '读输入寄存器 · 04H (3X·只读)',
})

export function modbusCmdLabel(value) {
  return MODBUS_CMD_LABEL[value] || (value != null ? String(value) : '-')
}

// 纯代号标签（无中文）：列表表格里紧凑展示功能码+数据区，如 "03H/06H/10H (4X)"。
export const MODBUS_CMD_CODE_LABEL = Object.freeze({
  [MODBUS_CMD.READ_COIL]:           '01H/05H (0X)',
  [MODBUS_CMD.READ_DISCRETE_INPUT]: '02H (1X)',
  [MODBUS_CMD.READ_HOLDING_REG]:    '03H/06H/10H (4X)',
  [MODBUS_CMD.READ_INPUT_REG]:      '04H (3X)',
})

export function modbusCmdCodeLabel(value) {
  return MODBUS_CMD_CODE_LABEL[value] || '-'
}

// -- 故障上报 ---------------------------------------------------------------
// 与后端 internal/repo/faultreport.go allowedFault* 一一对应；
// 任一边新增枚举值必须同步另一边，否则会被 binding 校验或前端渲染兜底吞掉。

export const FAULT_CATEGORY = Object.freeze({
  HARDWARE: 'hardware',
  DATA:     'data',
})

export const FAULT_CATEGORY_LABEL = Object.freeze({
  [FAULT_CATEGORY.HARDWARE]: { text: '硬件故障', tag: 'danger' },
  [FAULT_CATEGORY.DATA]:     { text: '数据异常', tag: 'warning' },
})

export const FAULT_SEVERITY = Object.freeze({
  URGENT: 'urgent',
  NORMAL: 'normal',
  LOW:    'low',
})

export const FAULT_SEVERITY_LABEL = Object.freeze({
  [FAULT_SEVERITY.URGENT]: { text: '紧急', tag: 'danger' },
  [FAULT_SEVERITY.NORMAL]: { text: '一般', tag: 'warning' },
  [FAULT_SEVERITY.LOW]:    { text: '建议', tag: 'info' },
})

// 上报对象由服务端按角色自动决定，不再让用户选：
//   子账号 user(有 parent) → tenant（送本租户管理员）；admin 与 super_admin 同样能看到
//   admin / 主账号 / super_admin → platform（仅送平台）
// LABEL 保留用于详情/列表展示历史工单去向。
export const FAULT_TARGET_LABEL = Object.freeze({
  platform: { text: '平台技术支持', tag: 'info' },
  tenant:   { text: '本公司管理员', tag: '' },
})

export const FAULT_STATUS = Object.freeze({
  PENDING:    'pending',
  PROCESSING: 'processing',
  RESOLVED:   'resolved',
  CLOSED:     'closed',
})

export const FAULT_STATUS_LABEL = Object.freeze({
  [FAULT_STATUS.PENDING]:    { text: '待处理', tag: 'warning' },
  [FAULT_STATUS.PROCESSING]: { text: '处理中', tag: 'info' },
  [FAULT_STATUS.RESOLVED]:   { text: '已解决', tag: 'success' },
  [FAULT_STATUS.CLOSED]:     { text: '已关闭', tag: '' },
})

// -- 消毒记录（牙椅水路清洗消毒，定制概况配套） -----------------------------
export const DISINFECTION_DURATION_MINUTES = 3
export const DISINFECTION_LIQUID_TEXT = '次氯酸消毒'

// 记录来源 / 执行方式
export const DISINFECT_SOURCE = Object.freeze({
  AUTO:    'auto',
  COMMAND: 'command',
  UPLOAD:  'upload',
})

export const DISINFECT_SOURCE_LABEL = Object.freeze({
  [DISINFECT_SOURCE.AUTO]:    { text: '自动消毒', tag: 'info' },
  [DISINFECT_SOURCE.COMMAND]: { text: '一键执行', tag: 'success' },
  [DISINFECT_SOURCE.UPLOAD]:  { text: '拍照上传', tag: 'warning' },
})

export const DISINFECT_SOURCE_OPTIONS = Object.freeze([
  { label: '自动消毒', value: DISINFECT_SOURCE.AUTO },
  { label: '一键执行', value: DISINFECT_SOURCE.COMMAND },
  { label: '拍照上传', value: DISINFECT_SOURCE.UPLOAD },
])

// 消毒内容（诊前 / 诊后）
export const DISINFECT_STAGE = Object.freeze({
  PRE:  'pre',
  POST: 'post',
})

export const DISINFECT_STAGE_LABEL = Object.freeze({
  [DISINFECT_STAGE.PRE]:  { text: '诊前冲洗', tag: '' },
  [DISINFECT_STAGE.POST]: { text: '诊后消毒', tag: 'info' },
})

export const DISINFECT_STAGE_OPTIONS = Object.freeze([
  { label: '诊前冲洗', value: DISINFECT_STAGE.PRE },
  { label: '诊后消毒', value: DISINFECT_STAGE.POST },
])

// 定制概况页仍在使用的统计状态；真实记录列表只返回已完成记录。
export const DISINFECT_STATUS = Object.freeze({
  DONE:     'done',
  ABNORMAL: 'abnormal',
})

export const DISINFECT_STATUS_LABEL = Object.freeze({
  [DISINFECT_STATUS.DONE]:     { text: '已消毒',   tag: 'success' },
  [DISINFECT_STATUS.ABNORMAL]: { text: '消毒异常', tag: 'danger' },
})

export const DISINFECTION_SCHEDULE_VERSION_STATUS = Object.freeze({
  CURRENT: 'current',
  PENDING: 'pending',
  EXPIRED: 'expired',
})

// 设备串口波特率（RS485/UART 总线属性，挂在设备而非点位模板，下发设备 config.baud）。
// 与后端 device.allowedBauds、固件 ALLOWED_BAUDS 白名单保持一致。
export const SERIAL_BAUD_DEFAULT = 9600

export const SERIAL_BAUD_OPTIONS = Object.freeze([
  { label: '1200',   value: 1200 },
  { label: '2400',   value: 2400 },
  { label: '4800',   value: 4800 },
  { label: '9600',   value: 9600 },
  { label: '19200',  value: 19200 },
  { label: '38400',  value: 38400 },
  { label: '57600',  value: 57600 },
  { label: '115200', value: 115200 },
])

/** OTA 当前任务状态；cancelled 不会被设备拉包检查重新激活。 */
export const OTA_STATUS = Object.freeze({
  PENDING: 'pending', DOWNLOADING: 'downloading', SUCCESS: 'success',
  FAILED: 'failed', CANCELLED: 'cancelled', IDLE: 'idle'
})
export const OTA_STATUS_META = Object.freeze({
  pending: { key: 'ota.pending', tag: 'warning' },
  downloading: { key: 'ota.downloading', tag: 'info' },
  success: { key: 'ota.success', tag: 'success' },
  failed: { key: 'ota.failed', tag: 'danger' },
  cancelled: { key: 'ota.cancelled', tag: 'info' },
  idle: { key: 'ota.idle', tag: 'info' }
})

/** Portrait dental signage: cadence and point names shared by controller and DTO mapping. */
export const DENTAL_SIGNAGE = Object.freeze({
  WIDTH: 1080, HEIGHT: 1920, REFRESH_MS: 30000, ROTATE_MS: 20000, PAGE_SIZE: 200,
  POINT_TEMPLATES: Object.freeze(['消毒采集模组', '消毒模组']),
  SOURCE_AUTO: 'auto', METHOD_PRE: 'pre', METHOD_POST: 'post',
  TOTAL_NAMES: Object.freeze(['总累计产量', '累计总产量', '累计产水量', '总产量', 'WaterCnt']),
  FLOW_NAMES: Object.freeze(['实时流量', 'L/min流量', 'WaterDischarge', 'waterDischarge']),
  PH_NAMES: Object.freeze(['实时Ph', '实时PH', 'PH', 'ph']),
  LEVEL_NAMES: Object.freeze(['实时液位', '液位', 'LiquidLevel', 'liquidLevel']),
  CONCENTRATION_NAMES: Object.freeze(['实时含量', 'Concentration']),
})

export const DENTAL_PORTRAIT = Object.freeze({
  FLUSH_POINT: '已冲洗', DISINFECT_POINT: '已消毒',
  COMPLETED: 1, PENDING: 0, RECORD_PAGE_MS: 12000, RECORD_VISIBLE_ROWS: 3, RECORD_SCROLL_SECONDS: 6, PENDING_PAGE_SIZE: 2,
  SOURCE_AUTO: 'auto', SOURCE_UPLOAD: 'upload', SOURCE_COMMAND: 'command', METHOD_PRE: 'pre',
})

export const DENTAL_LAYOUT_TYPE = Object.freeze({ LANDSCAPE: 'landscape', PORTRAIT: 'portrait' })
export const DENTAL_TEMPLATE_ID = Object.freeze({ LANDSCAPE: 7, PORTRAIT: 11 })

/** History table algorithms, export states and time units used by the API. */
export const HISTORY_ALGORITHM = Object.freeze({
  RAW: 'raw', DELTA: 'delta', AVG: 'avg', MAX: 'max', MIN: 'min', LAST: 'last', FIRST: 'first', SUM: 'sum', COUNT: 'count',
})
export const HISTORY_INTERVAL_UNITS = Object.freeze({ minute: 60, hour: 3600, day: 86400 })
export const HISTORY_INTERVAL_PRESETS = Object.freeze({ minute: [1, 5, 10, 15, 30], hour: [1, 3, 6, 12], day: [1, 7, 15, 30, 90] })
export const HISTORY_EXPORT_STATUS = Object.freeze({ QUEUED: 'queued', RUNNING: 'running', READY: 'ready', FAILED: 'failed' })
export const HISTORY_TABLE_LIMITS = Object.freeze({ points: 30, intervalDays: 90, rangeDays: 366 })
